import { Heart, ShoppingBag, Zap } from "lucide-react";

import StarRating from "@/theme1/components/testimonial/StarRating";
import ProductVariants from "@/theme1/components/product/ProductVariants";
import QuantitySelector from "@/theme1/components/product/QuantitySelector";

export default function ProductInfo({
  product,
  selectedColor,
  selectedSize,
  quantity,
  wished = false,
  onColorChange,
  onSizeChange,
  onQuantityChange,
  onAddToCart,
  onBuyNow,
  onWishlist,
}) {
  if (!product) return null;

  const soldOut =
    product.isSoldOut || Number(product.stock) <= 0;

  return (
    <aside className="lg:sticky lg:top-24">
      <div className="space-y-6">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.25em] text-muted-foreground">
            {product.series || "Captex"}
          </p>

          <h1 className="mt-3 max-w-xl text-3xl font-black uppercase leading-tight tracking-tight sm:text-4xl">
            {product.title}
          </h1>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <StarRating rating={product.rating || 0} />

          <span className="text-sm font-semibold">
            {product.rating || 0}
          </span>

          <span className="text-sm text-muted-foreground">
            {product.reviews || 0} reviews
          </span>
        </div>

        <div className="space-y-2">
          <div className="flex flex-wrap items-end gap-3">
            {product.comparePrice && (
              <span className="text-sm text-muted-foreground line-through">
                ৳
                {Number(
                  product.comparePrice
                ).toLocaleString()}
              </span>
            )}

            <span className="text-2xl font-black text-red-600">
              ৳{Number(product.price || 0).toLocaleString()}
            </span>
          </div>

          {product.discount && (
            <span className="inline-flex rounded-full bg-red-600 px-3 py-1 text-[10px] font-black uppercase text-white">
              Save {product.discount}% Off
            </span>
          )}
        </div>

        <div className="rounded-xl bg-amber-50 p-4">
          <p className="text-sm font-bold text-amber-900">
            Get a free legacy card with this cap and join
            the Captex Club.
          </p>
        </div>

        <ProductVariants
          product={product}
          selectedColor={selectedColor}
          selectedSize={selectedSize}
          onColorChange={onColorChange}
          onSizeChange={onSizeChange}
        />

        <QuantitySelector
          value={quantity}
          onChange={onQuantityChange}
          max={product.stock ?? 99}
        />

        <div className="space-y-3">
          <button
            type="button"
            onClick={onAddToCart}
            disabled={soldOut}
            aria-label={
              soldOut
                ? `${product.title} is sold out`
                : `Add ${product.title} to cart`
            }
            className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-black bg-white px-6 py-3 text-xs font-black uppercase text-black transition hover:bg-neutral-100 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 disabled:cursor-not-allowed disabled:border-neutral-300 disabled:bg-neutral-100 disabled:text-neutral-400"
          >
            <ShoppingBag className="size-4" />

            {soldOut ? "Sold Out" : "Add to Cart"}
          </button>

          <button
            type="button"
            onClick={onBuyNow}
            disabled={soldOut}
            aria-label={`Buy ${product.title} now`}
            className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-black px-6 py-3 text-xs font-black uppercase text-white transition hover:bg-neutral-800 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-neutral-300"
          >
            <Zap className="size-4" />

            Buy It Now
          </button>

          <button
            type="button"
            onClick={onWishlist}
            aria-pressed={wished}
            aria-label={
              wished
                ? `Remove ${product.title} from wishlist`
                : `Add ${product.title} to wishlist`
            }
            className={`inline-flex w-full items-center justify-center gap-2 rounded-lg border px-6 py-3 text-xs font-black uppercase transition focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 ${
              wished
                ? "border-red-200 bg-red-50 text-red-600"
                : "border-border bg-white text-foreground hover:bg-neutral-100"
            }`}
          >
            <Heart
              className={`size-4 transition ${
                wished
                  ? "fill-red-600 text-red-600"
                  : ""
              }`}
            />

            {wished
              ? "Remove from Wishlist"
              : "Add to Wishlist"}
          </button>
        </div>

        <div className="grid grid-cols-3 gap-3 pt-4 text-center">
          <InfoItem title="Cash on Delivery" />
          <InfoItem title="Premium Packaging" />
          <InfoItem title="Dispatch 2–3 Days" />
        </div>
      </div>
    </aside>
  );
}

function InfoItem({ title }) {
  return (
    <div className="rounded-xl bg-neutral-100/50 p-3">
      <p className="text-[11px] font-bold leading-tight">
        {title}
      </p>
    </div>
  );
}